import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutPage() {
  const { items, subtotal, bundleDiscount, shipping, total, selectedGift, setGiftPickerOpen } = useCart();
  const { format, code: currencyCode } = useCurrency();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const startCheckout = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error('Your bag is empty.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          pack_size: i.pack_size,
          quantity: i.quantity,
        })),
        currency: currencyCode,
        gift_id: selectedGift?.id || null,
        email: email || null,
        origin_url: window.location.origin,
      };
      const { data } = await axios.post(`${API}/checkout/create-session`, payload);
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error('Could not start checkout.');
        setSubmitting(false);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Checkout could not be started.';
      toast.error(typeof detail === 'string' ? detail : 'Checkout could not be started.');
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div data-testid="checkout-empty" className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif-display text-5xl mb-4">Your bag is empty.</h1>
        <Link to="/shop" className="btn-primary mt-4 inline-flex">Shop the masks</Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-12">
      <div className="md:col-span-7">
        <p className="overline mb-3">Secure checkout</p>
        <h1 className="font-serif-display text-5xl md:text-6xl leading-[1.02] mb-10">One last step.</h1>

        <form onSubmit={startCheckout} className="space-y-8">
          <div>
            <p className="overline mb-5">Your email <span className="text-lum-muted normal-case tracking-normal">(optional)</span></p>
            <input
              data-testid="checkout-email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-line"
            />
            <p className="text-xs text-lum-muted mt-3">
              We&apos;ll send your receipt and tracking here. You can also enter it on the next screen.
            </p>
          </div>

          <div className="bg-lum-bg2 p-6 border border-lum-border space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <Lock size={14} className="text-lum-sale" />
              <span className="font-medium">Pay securely with Stripe</span>
            </div>
            <p className="text-xs text-lum-text2 leading-relaxed">
              Cards, Apple Pay and Google Pay accepted. You&apos;ll enter your shipping address and payment on Stripe&apos;s secure page, then return here.
            </p>
          </div>

          <button data-testid="checkout-place-order" disabled={submitting} className="btn-primary w-full">
            {submitting ? 'Redirecting to Stripe…' : `Pay ${format(total)} with Stripe`}
          </button>

          <p className="text-xs text-lum-muted text-center">
            🔒 Powered by Stripe · We never see your card details
          </p>
        </form>
      </div>

      <aside className="md:col-span-5 bg-lum-bg2 p-8 md:p-10 h-fit md:sticky md:top-28">
        <p className="overline mb-5">Your order</p>
        <ul className="space-y-4 max-h-[40vh] overflow-auto pr-2">
          {items.map((i) => (
            <li key={i.key} className="flex gap-4">
              <div className={`w-16 h-20 ${i.accent === 'blue' ? 'accent-blue-soft' : i.accent === 'green' ? 'accent-green-soft' : 'accent-orange-soft'} flex-shrink-0`}>
                <img src={i.image} alt={i.name} className="w-full h-full object-contain p-1.5" />
              </div>
              <div className="flex-1">
                <p className="font-serif-display text-lg leading-tight">{i.name}</p>
                <p className="text-xs text-lum-muted">{i.pack_size === 'pack' ? `Pack of ${i.pack_count}` : 'Single'} × {i.quantity}</p>
              </div>
              <p className="text-sm">{format(i.unit_price * i.quantity)}</p>
            </li>
          ))}
        </ul>
        <div className="border-t border-lum-border mt-6 pt-4 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-lum-text2">Subtotal</span><span>{format(subtotal)}</span></div>
          {bundleDiscount > 0 && <div className="flex justify-between text-lum-sale"><span>Bundle (10%)</span><span>−{format(bundleDiscount)}</span></div>}
          <div className="flex justify-between"><span className="text-lum-text2">Shipping</span><span>{shipping === 0 ? 'Free' : format(shipping)}</span></div>
          <div className="flex justify-between text-base border-t border-lum-border pt-3 mt-3"><span>Total · {currencyCode}</span><span>{format(total)}</span></div>
        </div>
        <p className="text-xs text-lum-muted mt-6">+ Free gift included with every order.</p>
        {selectedGift ? (
          <div data-testid="checkout-selected-gift" className="mt-4 flex items-center gap-3 p-3 bg-lum-bg border border-lum-border">
            <div className={`w-12 h-12 ${selectedGift.accent === 'green' ? 'accent-green-soft' : 'accent-orange-soft'} flex-shrink-0`}>
              <img src={selectedGift.image} alt={selectedGift.name} className="w-full h-full object-contain p-1" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] text-lum-sale">Your free gift</p>
              <p className="text-sm leading-tight truncate">{selectedGift.name}</p>
            </div>
            <button type="button" onClick={() => setGiftPickerOpen(true)} className="text-xs uppercase tracking-[0.18em] underline underline-offset-4">Change</button>
          </div>
        ) : (
          <button
            type="button"
            data-testid="checkout-pick-gift"
            onClick={() => setGiftPickerOpen(true)}
            className="mt-4 w-full p-3 border border-dashed border-lum-text bg-lum-bg2/60 hover:bg-lum-bg2 transition-colors text-xs uppercase tracking-[0.22em] text-lum-sale"
          >
            ✦ Pick your free gift
          </button>
        )}
      </aside>
    </div>
  );
}
